import { Navigate, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";
import { useSelector } from "react-redux";
import type { FC, ReactNode } from "react";
import type { RootState } from "../../store/store";
import { getPostLoginDestination } from "@utils/postAuthRedirect";

interface GuestGuardProps {
	children: ReactNode;
}

/**
 * When auth flips true while still on a guest route (e.g. right after login),
 * send the user to the same destination as post-login navigate — including
 * `location.state.from` from AuthGuard (not only `?redirect=` on the URL).
 * Otherwise we briefly render with isAuthenticated and wrongly send users to `/`.
 */
const GuestGuard: FC<GuestGuardProps> = ({ children }) => {
	const { isAuthenticated } = useSelector((state: RootState) => state.user);
	const location = useLocation();

	if (isAuthenticated) {
		const stateFrom = (location.state as { from?: Location } | null)?.from;
		const next = getPostLoginDestination({
			search: location.search,
			stateFrom,
		});
		return <Navigate to={next} replace />;
	}
	return <>{children}</>;
};

export default GuestGuard; 